import type { ReactNode } from "react";

import { AdminRolesDialog } from "../pages/admin/dashboard/_dialogs/AdminRolesDialog";
import { CreateCouponDialog } from "../pages/admin/dashboard/_dialogs/create-coupon";
import { CreateCreditDialog } from "../pages/admin/dashboard/_dialogs/create-credit";
import { CreatePaymentProviderDialog } from "../pages/admin/dashboard/_dialogs/create-payment-provider";
import { CreateProviderZoneDialog } from "../pages/admin/dashboard/_dialogs/create-provider-zone";
import { CreateRateDialog } from "../pages/admin/dashboard/_dialogs/create-rate";
import { ManualCreateUserDialog } from "../pages/admin/dashboard/_dialogs/create-user";
import { EditPaymentProviderDialog } from "../pages/admin/dashboard/_dialogs/edit-payment-provider";
import { EditProviderZoneDialog } from "../pages/admin/dashboard/_dialogs/edit-provider-zone";
import { FilterAdminDialog } from "../pages/admin/dashboard/_dialogs/filter-admin";
import { FilterAmbassadorDialog } from "../pages/admin/dashboard/_dialogs/filter-ambassador";
import { FilterBonusRefDialog } from "../pages/admin/dashboard/_dialogs/filter-bonus";
import { FilterClientDialog } from "../pages/admin/dashboard/_dialogs/filter-user";
import { FilterCouponDialog } from "../pages/admin/dashboard/_dialogs/filter-coupon";
import { FilterDownloadDialog } from "../pages/admin/dashboard/_dialogs/filter-download";
import { FilterPaymentDialog } from "../pages/admin/dashboard/_dialogs/filter-payment";
import { FilterRateDialog } from "../pages/admin/dashboard/_dialogs/filter-rate";
import { FilterReferenceDialog } from "../pages/admin/dashboard/_dialogs/filter-references";
import { FilterResumeDialog } from "../pages/admin/dashboard/_dialogs/filter-resumes";
import { FilterWithdrawalDialog } from "../pages/admin/dashboard/_dialogs/filter-withdrawal";
import { GetCouponDialog } from "../pages/admin/dashboard/_dialogs/view-coupon";
import { GetPaymentDialog } from "../pages/admin/dashboard/_dialogs/view-payment";
import { GetUserDialog } from "../pages/admin/dashboard/_dialogs/view-user";
import { GetWithdrawalDialog } from "../pages/admin/dashboard/_dialogs/view-withdrawal";
import { HelpDetailDialog } from "../pages/admin/dashboard/_dialogs/help-detail";
import { FilterCouponAmbaDialog } from "../pages/ambassador/dashboard/_dialogs/filter-coupon";
import { FilterTransactionDialog } from "../pages/ambassador/dashboard/_dialogs/filter-transaction";
import { FilterWithdrawalForAmbassadorDialog } from "../pages/ambassador/dashboard/_dialogs/filter-withdrawal";
import { MyWithdrawalDialog } from "../pages/ambassador/dashboard/_dialogs/list-my-withdrawal";
import { RequestWithdrawalDialog } from "../pages/ambassador/dashboard/_dialogs/request-withdrawal";
import { DialogTemplate } from "../pages/client/builder/_components/dialog-template";
import { AiPacksDialog } from "../pages/client/builder/dialogs/ai-packs-dialog";
import { AlertCreditBalance } from "../pages/client/builder/dialogs/alert-credit-balance";
import { ConfirmDownloadDialog } from "../pages/client/builder/dialogs/confirm-download";
import { ForfaitDialog } from "../pages/client/builder/dialogs/forfait-modal";
import { PaymentDialog } from "../pages/client/builder/dialogs/payment";
import { DialogScoring } from "../pages/client/builder/dialogs/scoring-dialog";
import { DialogTranslationI } from "../pages/client/builder/dialogs/translate";
import { AwardsDialog } from "../pages/client/builder/sidebars/left/dialogs/awards";
import { CertificationsDialog } from "../pages/client/builder/sidebars/left/dialogs/certifications";
import { CustomSectionDialog } from "../pages/client/builder/sidebars/left/dialogs/custom-section";
import { EducationDialog } from "../pages/client/builder/sidebars/left/dialogs/education";
import { ExperienceDialog } from "../pages/client/builder/sidebars/left/dialogs/experience";
import { InterestsDialog } from "../pages/client/builder/sidebars/left/dialogs/interests";
import { LanguagesDialog } from "../pages/client/builder/sidebars/left/dialogs/languages";
import { ProfilesDialog } from "../pages/client/builder/sidebars/left/dialogs/profiles";
import { ProjectsDialog } from "../pages/client/builder/sidebars/left/dialogs/projects";
import { PublicationsDialog } from "../pages/client/builder/sidebars/left/dialogs/publications";
import { ReferencesDialog } from "../pages/client/builder/sidebars/left/dialogs/references";
import { SkillsDialog } from "../pages/client/builder/sidebars/left/dialogs/skills";
import { VolunteerDialog } from "../pages/client/builder/sidebars/left/dialogs/volunteer";
import { ImportDialog } from "../pages/client/dashboard/resumes/_dialogs/import";
import { ResumeDialog } from "../pages/client/dashboard/resumes/_dialogs/resume";
import { ImportLinkedinDialog } from "../pages/client/user-space/documents/resumes/import-linkedin-dialog";
import { ImportOcrDialog } from "../pages/client/user-space/documents/resumes/import-ocr-dialog";
import { useUser } from "../services/user";
import { useResumeStore } from "../stores/resume";

type Props = {
  children: ReactNode;
};

export const DialogProvider = ({ children }: Props) => {
  const isResumeLoaded = useResumeStore((state) => Object.keys(state.resume).length > 0);

  const { user } = useUser();

  return (
    <>
      {children}

      <div id="dialog-root">
        <ResumeDialog />
        <ImportDialog />
        <ImportOcrDialog />
        <ImportLinkedinDialog />

        {/*
         * Paiements et catalogues.
         *
         * ForfaitDialog :
         * offres CV uniquement.
         *
         * AiPacksDialog :
         * crédits IA uniquement.
         */}
        <PaymentDialog />
        <ForfaitDialog />
        <AiPacksDialog />
        <AlertCreditBalance />

        {user?.isInternal && (
          <div data-space="admin">
            <GetUserDialog />
            <FilterClientDialog />
            <FilterAmbassadorDialog />
            <FilterAdminDialog />
            <ManualCreateUserDialog />
            <CreateCreditDialog />
            <FilterResumeDialog />
            <FilterReferenceDialog />
            <FilterBonusRefDialog />
            <CreateRateDialog />
            <FilterRateDialog />
            <FilterCouponDialog />
            <GetCouponDialog />
            <CreateCouponDialog />
            <FilterPaymentDialog />
            <FilterWithdrawalDialog />
            <FilterDownloadDialog />
            <GetPaymentDialog />
            <GetWithdrawalDialog />
            <CreatePaymentProviderDialog />
            <EditPaymentProviderDialog />
            <EditProviderZoneDialog />
            <CreateProviderZoneDialog />
            <AdminRolesDialog />
            <HelpDetailDialog />
          </div>
        )}

        {isResumeLoaded && (
          <div data-space="client">
            <ProfilesDialog />
            <ExperienceDialog />
            <EducationDialog />
            <AwardsDialog />
            <CertificationsDialog />
            <InterestsDialog />
            <LanguagesDialog />
            <ProjectsDialog />
            <PublicationsDialog />
            <VolunteerDialog />
            <SkillsDialog />
            <ReferencesDialog />
            <CustomSectionDialog />

            <DialogTemplate />
            <DialogTranslationI />
            <DialogScoring />
            <ConfirmDownloadDialog />
          </div>
        )}

        {user && (
          <div data-space="ambassador">
            <MyWithdrawalDialog />
            <RequestWithdrawalDialog />
            <FilterTransactionDialog />
            <FilterWithdrawalForAmbassadorDialog />
            <FilterCouponAmbaDialog />
          </div>
        )}
      </div>
    </>
  );
};
